/**
 * Server-aligned clock for online sessions.
 *
 * - Fetches /current-time-ist on page load and when the tab becomes visible again.
 * - Parses session start in the timezone from the server response.
 * - Start/Join gate arms one timeout until the soonest window opens (start − buffer).
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { authService } from "./authService";

/** Early Start/Join window (existing product rule). */
export const EARLY_START_BUFFER_MS = 5 * 60 * 1000;

const MAX_TIMEOUT_MS = 2_147_483_647;
const DEFAULT_TZ_SUFFIX = "+05:30";

export type SessionStartSlot = {
  date: string;
  time: string;
};

type ServerTimePayload = {
  datetime?: string;
  timezone?: string;
  timestamp?: number;
};

type ServerClock = {
  offsetMs: number;
  tzSuffix: string;
};

const MONTHS: Record<string, string> = {
  jan: "01",
  feb: "02",
  mar: "03",
  apr: "04",
  may: "05",
  jun: "06",
  jul: "07",
  aug: "08",
  sep: "09",
  oct: "10",
  nov: "11",
  dec: "12",
};

function tzSuffixFromDatetime(iso: string | undefined): string {
  if (!iso) return DEFAULT_TZ_SUFFIX;
  const m = String(iso).trim().match(/(Z|[+-]\d{2}:\d{2})$/i);
  if (!m) return DEFAULT_TZ_SUFFIX;
  return m[1].toUpperCase() === "Z" ? "+00:00" : m[1];
}

function normalizeDateToYmd(dateStr: string): string | null {
  const s = String(dateStr || "").trim();
  if (!s) return null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  const m = s.match(/^(\d{1,2})\s+([A-Za-z]{3})\s+(\d{4})$/);
  if (!m) return null;
  const mm = MONTHS[m[2].toLowerCase()];
  if (!mm) return null;
  return `${m[3]}-${mm}-${String(Number(m[1])).padStart(2, "0")}`;
}

function normalizeTimeToHms(timeStr: string): string | null {
  const raw = String(timeStr || "").trim();
  if (!raw) return null;

  const twelve = raw.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?\s*(AM|PM)$/i);
  if (twelve) {
    let hours = Number(twelve[1]) % 12;
    if (/PM/i.test(twelve[4])) hours += 12;
    const minutes = Number(twelve[2]);
    const seconds = Number(twelve[3] || 0);
    if (minutes > 59 || seconds > 59) return null;
    return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:${String(
      seconds,
    ).padStart(2, "0")}`;
  }

  const twentyFour = raw.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
  if (!twentyFour) return null;
  const hours = Number(twentyFour[1]);
  const minutes = Number(twentyFour[2]);
  const seconds = Number(twentyFour[3] || 0);
  if (hours > 23 || minutes > 59 || seconds > 59) return null;
  return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:${String(
    seconds,
  ).padStart(2, "0")}`;
}

export function parseScheduledSessionMs(
  dateStr: string,
  timeStr: string,
  tzSuffix: string,
): number | null {
  const ymd = normalizeDateToYmd(dateStr);
  const hms = normalizeTimeToHms(timeStr);
  if (!ymd || !hms) return null;
  const ms = Date.parse(`${ymd}T${hms}${tzSuffix}`);
  return Number.isNaN(ms) ? null : ms;
}

async function fetchServerClock(): Promise<ServerClock | null> {
  try {
    const response = await authService.get("/current-time-ist");
    const data = response.data as ServerTimePayload;
    let serverMs: number | null = null;
    if (typeof data?.timestamp === "number" && Number.isFinite(data.timestamp)) {
      serverMs = data.timestamp * 1000;
    } else if (data?.datetime) {
      const parsed = Date.parse(data.datetime);
      serverMs = Number.isNaN(parsed) ? null : parsed;
    }
    if (serverMs == null) return null;
    return {
      offsetMs: serverMs - Date.now(),
      tzSuffix: tzSuffixFromDatetime(data.datetime),
    };
  } catch {
    return null;
  }
}

function nowFromClock(clock: ServerClock): number {
  return Date.now() + clock.offsetMs;
}

function useServerClockSync(): ServerClock | null {
  const [clock, setClock] = useState<ServerClock | null>(null);

  const syncClock = useCallback(async () => {
    const next = await fetchServerClock();
    if (next) setClock(next);
  }, []);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const next = await fetchServerClock();
      if (!cancelled && next) setClock(next);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === "visible") void syncClock();
    };
    const onPageShow = (e: PageTransitionEvent) => {
      if (e.persisted) void syncClock();
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("pageshow", onPageShow);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("pageshow", onPageShow);
    };
  }, [syncClock]);

  return clock;
}

/**
 * Offset to add to Date.now() for server-aligned time.
 * 0 until the first /current-time-ist sync succeeds.
 */
export function useServerClockOffsetMs(): number {
  const clock = useServerClockSync();
  return clock?.offsetMs ?? 0;
}

/**
 * @param startSlots scheduled session starts to arm wake-ups for
 * @param bufferMs early window before start (default 5 minutes)
 */
export function useSessionStartGate(
  startSlots: SessionStartSlot[],
  bufferMs: number = EARLY_START_BUFFER_MS,
): {
  clockReady: boolean;
  /** Bumps when a gate opens or clock re-syncs — use to refresh UI. */
  gateVersion: number;
  canStartSessionAt: (dateStr: string, timeStr: string) => boolean;
} {
  const clock = useServerClockSync();
  const [gateVersion, setGateVersion] = useState(0);
  const [syncedOffset, setSyncedOffset] = useState<number | null>(null);

  useEffect(() => {
    if (!clock) return;
    if (syncedOffset === clock.offsetMs) return;
    setSyncedOffset(clock.offsetMs);
    setGateVersion((v) => v + 1);
  }, [clock, syncedOffset]);

  const slotsKey = useMemo(() => {
    return startSlots
      .map((s) => `${String(s.date || "").trim()}|${String(s.time || "").trim()}`)
      .filter((k) => k !== "|")
      .sort()
      .join(";");
  }, [startSlots]);

  const readyAtMsList = useMemo(() => {
    const tz = clock?.tzSuffix ?? DEFAULT_TZ_SUFFIX;
    const times: number[] = [];
    for (const slot of startSlots) {
      const startMs = parseScheduledSessionMs(slot.date, slot.time, tz);
      if (startMs != null) times.push(startMs - bufferMs);
    }
    return times;
    // slotsKey captures startSlots content; clock tz updates re-parse.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slotsKey, clock?.tzSuffix, bufferMs]);

  useEffect(() => {
    if (!clock) return;
    const now = nowFromClock(clock);
    let soonest = Number.POSITIVE_INFINITY;
    for (const t of readyAtMsList) {
      if (t > now) soonest = Math.min(soonest, t);
    }
    if (!Number.isFinite(soonest)) return;

    const delay = Math.min(Math.max(0, soonest - now) + 50, MAX_TIMEOUT_MS);
    const id = window.setTimeout(() => setGateVersion((v) => v + 1), delay);
    return () => window.clearTimeout(id);
  }, [clock, readyAtMsList, gateVersion]);

  const canStartSessionAt = useCallback(
    (dateStr: string, timeStr: string) => {
      if (!clock) return false;
      const startMs = parseScheduledSessionMs(dateStr, timeStr, clock.tzSuffix);
      if (startMs == null) return false;
      return nowFromClock(clock) >= startMs - bufferMs;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [clock, bufferMs, gateVersion],
  );

  return {
    clockReady: clock != null,
    gateVersion,
    canStartSessionAt,
  };
}
